<template>
    <message-hub-card-stack :count="props.threads.length" @clear="removeGroupThreads">
        <template v-slot:title>
            <div class="flex flex-wrap flex-row items-center gap-2 md:flex-row-reverse">
                <vx-popover hover placement="top" arrow>
                    <span class="text-lg font-bold text-slate-800 cursor-pointer">
                        {{ pluralized('thread', props.threads.length, true) }}
                    </span>
                    <template v-slot:content>
                        <div class="flex flex-col gap-2 max-w-xs p-2">
                            <div class="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                                <template v-for="thread in threadsToShow" :key="thread.contact_id">
                                    <strong>{{ thread.contact_name || '-' }}</strong>
                                    <span>{{
                                        thread.contact_phone ? formatPhoneNumber(thread.contact_phone) : '-'
                                    }}</span>
                                </template>
                            </div>
                            <vx-button
                                v-if="props.threads.length > MAX_POPOVER_CONTACTS"
                                size="xs"
                                color="muted-light"
                                @click="showContactsModal = true"
                            >
                                View All
                                <font-awesome-icon widthAuto :icon="faEye" />
                            </vx-button>
                        </div>
                    </template>
                </vx-popover>

                <vx-badge color="amber" size="md">
                    <font-awesome-icon widthAuto :icon="faGrid2Plus" />
                    {{ groupName }}
                </vx-badge>
            </div>
        </template>
        <vx-box color="info">
            <div class="p-6 flex flex-col gap-4">
                <div class="rounded-xl overflow-hidden bg-white border border-solid border-slate-200 flex">
                    <button
                        type="button"
                        @click="emit('openSnippetModal', props.threads)"
                        data-test="close-with-snippet-button"
                        class="appearance-none text-slate-800 bg-transparent flex-1 gap-1 flex items-center justify-center py-3 px-4 border-r border-solid border-slate-200 border-l-0 border-y-0 hover:bg-slate-50 active:bg-slate-100 cursor-pointer"
                    >
                        <font-awesome-icon widthAuto :icon="faMessageMinus" class="text-sm h-3.5" />
                        <span class="text-xs text-slate-500 font-semibold">Close w/ Snippet</span>
                    </button>

                    <button
                        v-if="hasScope('threads:assign')"
                        type="button"
                        @click="emit('openAssignModal', props.threads)"
                        data-test="assign-button"
                        class="appearance-none text-slate-800 bg-transparent flex-1 gap-1 flex items-center justify-center py-3 px-4 border-r border-solid border-slate-200 border-l-0 border-y-0 hover:bg-slate-50 active:bg-slate-100 cursor-pointer"
                    >
                        <font-awesome-icon widthAuto :icon="faUserPen" class="text-sm h-3.5" />
                        <span class="text-xs text-slate-500 font-semibold">Assign</span>
                    </button>

                    <vx-dropdown placement="right" teleport>
                        <button
                            type="button"
                            data-test="more-actions-button"
                            aria-label="More actions"
                            class="appearance-none text-slate-800 bg-transparent gap-1 flex items-center justify-center py-3 px-4 hover:bg-slate-50 active:bg-slate-100"
                        >
                            <font-awesome-icon widthAuto :icon="faEllipsis" class="text-sm h-3.5" />
                        </button>
                        <template v-slot:items>
                            <vx-dropdown-item
                                v-for="action in dropdownActions"
                                v-show="action.show"
                                :key="action.id"
                                :data-test="`${action.id}-action`"
                                @click="action.handler"
                            >
                                {{ action.label }}
                                <font-awesome-icon widthAuto :icon="action.icon" :class="action.color" />
                            </vx-dropdown-item>
                        </template>
                    </vx-dropdown>
                </div>

                <message-builder
                    v-model="groupMessage"
                    v-model:mediaUrl="groupMediaUrl"
                    v-model:mediaContentType="groupMediaContentType"
                    :disabled="sendingMessage"
                    placeholder="Message multiple contacts..."
                    @submit="sendGroupMessage"
                    @schedule="scheduleGroupMessage"
                    :blockImages="blockImages"
                    :blockFiles="blockFiles"
                    maxHeight
                />
            </div>

            <message-hub-selected-contacts-modal
                v-model:visible="showContactsModal"
                :groupName="groupName"
                :threads="props.threads"
            />
        </vx-box>
    </message-hub-card-stack>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { faEllipsis, faEye, faGrid2Plus, faMessageMinus, faUserPen } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxBadge, VxBox, VxButton, VxDropdown, VxDropdownItem, VxPopover } from '@voxie/frontend-components';
import { pluralized, formatPhoneNumber } from '~/components/filters';
import MessageBuilder from '~/components/general/message-builder/MessageBuilder.vue';
import MessageHubCardStack from './MessageHubCardStack.vue';
import MessageHubSelectedContactsModal from './MessageHubSelectedContactsModal.vue';
import { useMessageHub } from '../useMessageHub';
import { useMessageHubBulkActions } from './useMessageHubBulkActions';
import { hasScope } from '~/utils/users';
import type { Group } from '~/services/groups/groupsClient.types';
import type { Thread } from '~/services/threads/threadsClient.types';
import { useToasted } from '~/composables/useToasted';
import axios from 'axios';
import type { MessageHubRuleRestrictions } from '~/types/messageRules.types';

const MAX_POPOVER_CONTACTS = 6;

interface Props {
    groupId: string;
    threads: Thread[];
}

const props = defineProps<Props>();

const emit = defineEmits<{
    openSnippetModal: [threads: Thread[]];
    openAssignModal: [threads: Thread[]];
}>();

const toasted = useToasted();
const { groups, removeThreadSelection } = useMessageHub();
const { sendBulkMessage, scheduleBulkMessage, getActionsForThreads, sendingMessage } = useMessageHubBulkActions();

const groupMessage = ref('');
const groupMediaUrl = ref<string | null>(null);
const groupMediaContentType = ref<string | null>(null);
const showContactsModal = ref(false);

const teamId = Spark.state.currentTeam.id;
const rules = ref<MessageHubRuleRestrictions | null>(null);

watch(
    () => props.groupId,
    async (groupId) => {
        if (!groupId) {
            rules.value = null;
            return;
        }
        try {
            const response = await axios.get(`/api/v3/teams/${teamId}/groups/${groupId}/message-hub/rules`);
            rules.value = response.data.rules;
        } catch {
            rules.value = null;
        }
    },
    { immediate: true }
);

const blockImages = computed(() => rules.value?.block_images ?? false);
const blockFiles = computed(() => rules.value?.block_files ?? false);

const groupName = computed(() => {
    const group = groups.value.find((g: Group) => String(g.group_id) === String(props.groupId));
    return group?.name || props.groupId;
});

const threadsToShow = computed(() => props.threads.slice(0, MAX_POPOVER_CONTACTS));

const dropdownActions = computed(() => {
    return getActionsForThreads(props.threads).filter(
        (action) => action.id !== 'close-with-snippet' && action.id !== 'assign'
    );
});

const removeGroupThreads = () => {
    props.threads.forEach((thread) => {
        removeThreadSelection(thread);
    });
};

const resetGroupMessage = () => {
    groupMessage.value = '';
    groupMediaUrl.value = null;
    groupMediaContentType.value = null;
};

const sendGroupMessage = async () => {
    if (!groupMessage.value.trim()) {
        toasted.error('Message body is required.');
        return;
    }

    const messageData = {
        body: groupMessage.value,
        media_url: groupMediaUrl.value,
        media_content_type: groupMediaContentType.value,
    };

    await sendBulkMessage(props.threads, messageData);
    resetGroupMessage();
};

const scheduleGroupMessage = async (scheduleAt: string) => {
    if (!groupMessage.value.trim()) {
        return;
    }

    const messageData = {
        body: groupMessage.value,
        media_url: groupMediaUrl.value,
        media_content_type: groupMediaContentType.value,
    };

    await scheduleBulkMessage(scheduleAt, props.threads, messageData);
    resetGroupMessage();
};
</script>
